import React from "react";
import Screen from "../uizard/ui/Screen";

export default function Privacy() {
  return (
    <Screen>
      <StyleTag />
      <div className="legal-wrap">
        <header className="legal-header">
          <h1 className="h1">Privacy Policy</h1>
          <p className="lead">Effective Date: September 5, 2025</p>
          <p className="entity">
            Entity: <b>ArtiBytes</b> (“ArtiBytes,” “we,” “us,” “our”)
          </p>
          <p className="intro">
            ArtiBytes respects your privacy and protects your personal information. This
            Privacy Policy explains what we collect, how we use it, and your rights. See
            also our <a href="/terms">Terms of Service</a> and{" "}
            <a href="/content-policy">Content Policy</a>.
          </p>
        </header>

        <section className="legal-section">
          <h2 className="h2">1) Information We Collect</h2>

          <h3>Account &amp; Identity</h3>
          <ul>
            <li>Google account identifier (e.g., a Google user ID) used to sign you in</li>
            <li>Display name and profile image (if provided by Google and enabled by you)</li>
            <li>Email address (if provided by Google and required for account or receipts)</li>
          </ul>

          <h3>Payments, Credits &amp; Payouts</h3>
          <ul>
            <li>Credit purchases and payment status (e.g., successful/failed/refunded)</li>
            <li>Transaction references and receipts (e.g., Stripe IDs)</li>
            <li>Royalty/earnings records related to the Creator Program (if you participate)</li>
            <li>Payout details you provide (e.g., a wallet address), if payouts are enabled</li>
          </ul>

          <h3>Usage &amp; Activity</h3>
          <ul>
            <li>Generation activity (prompts, parameters, timestamps, success/failure)</li>
            <li>Credits usage and feature interactions</li>
            <li>Creator Style submissions (metadata, descriptions, and submission status)</li>
          </ul>

          <h3>Content</h3>
          <ul>
            <li>Images you upload for generation, reference, or style training</li>
            <li>Generated images you create using the service</li>
            <li>Feedback, ratings, support requests, or reports you submit</li>
          </ul>

          <h3>Device/Technical</h3>
          <ul>
            <li>IP address, device/browser type, OS, language, app version</li>
            <li>Log data and performance metrics (e.g., latency, errors)</li>
          </ul>

          <h3>Sensitive data</h3>
          <p>
            We do not intentionally collect sensitive personal data. Please do not upload
            sensitive personal information in prompts or images. If a specific feature
            requires limited additional data (for example, payout details), we’ll tell you
            and process it only for that purpose.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="h2">2) Why We Process Your Data (GDPR Legal Bases)</h2>
          <ul>
            <li>
              <b>Provide the service (contract):</b> enable image generation, credit
              purchases, account features, Style submission, and Creator Program features.
            </li>
            <li>
              <b>Improve and secure (legitimate interests):</b> prevent abuse, debug,
              optimize performance, measure usage, and maintain platform integrity.
            </li>
            <li>
              <b>Comply with law (legal obligation):</b> maintain required records (e.g.,
              accounting), respond to lawful requests, and prevent fraud.
            </li>
            <li>
              <b>Consent (where required):</b> optional communications or features you opt
              into, and non-essential cookies where required by law.
            </li>
          </ul>
        </section>

        <section className="legal-section">
          <h2 className="h2">3) How We Use Your Information</h2>
          <ul>
            <li>Operate and improve image generation, style training/hosting, and account features.</li>
            <li>Process purchases, manage credits, and provide receipts and transaction history.</li>
            <li>Operate the Creator Program (Style review, publishing, royalty accounting, payouts if enabled).</li>
            <li>Prevent abuse and enforce our Terms and Content Policy.</li>
            <li>Provide support and notify you about important changes.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2 className="h2">4) Data Storage, Location &amp; Security</h2>
          <ul>
            <li>We host ArtiBytes on trusted providers (for example, Google Cloud / Cloud Run).</li>
            <li>
              Data may be processed or stored in the EEA and other countries. When data
              leaves the EEA, we use appropriate safeguards (for example, Standard
              Contractual Clauses).
            </li>
            <li>
              We apply technical and organizational measures such as encryption in transit,
              access controls, least-privilege, and monitoring. No system is 100% secure.
            </li>
          </ul>
        </section>

        <section className="legal-section">
          <h2 className="h2">5) Retention</h2>
          <ul>
            <li>
              <b>Account data:</b> retained while you maintain an account and as needed for
              service operations.
            </li>
            <li>
              <b>Transaction records:</b> retained as required by law and for accounting,
              audit, and dispute handling (commonly 6–10 years depending on jurisdiction).
            </li>
            <li>
  <b>Generated images:</b> images you generate are stored and associated with your
  account and remain available to you unless and until you delete them. When you
  delete an image, it is permanently removed from active systems, subject only to
  limited retention in backups or logs for legal, security, or integrity purposes.
</li>

            <li>
              <b>Uploaded assets for Style training:</b> retained while your Style is
              active; minimal artifacts may be retained after removal/termination to meet
              legal, compliance, audit, security, dispute resolution, or operational needs
              (see Creator Agreement).
            </li>
          </ul>
        </section>

        <section className="legal-section">
          <h2 className="h2">6) Sharing of Information</h2>
          <p>We do not sell or rent your personal data. We share only as needed:</p>
          <ul>
            <li>
              <b>Service providers / processors:</b> cloud hosting, storage, security, and
              related infrastructure providers that process data on our instructions.
            </li>
            <li>
              <b>Payments:</b> payment processors and related tools used to process purchases
              and handle refunds/chargebacks (for example, Stripe).
            </li>
            <li>
              <b>Legal/Compliance:</b> regulators, courts, or parties as required by law or
              to protect rights, safety, and integrity of the platform.
            </li>
          </ul>
          <p>
            Our service providers are bound by contracts and process personal data only on
            our instructions.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="h2">7) Your Rights (EU/EEA)</h2>
          <p>
            Depending on your location, you may have the right to access, rectify, erase,
            restrict, or object to processing, and to data portability. You may withdraw
            consent at any time where processing relies on consent. Request these rights or
            data deletion at:{" "}
            <a href="mailto:jaytayeldev@gmail.com">jaytayeldev@gmail.com</a>. You may lodge
            a complaint with your local data protection authority.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="h2">8) Children</h2>
          <p>
            ArtiBytes is not directed to children under 16. Do not use ArtiBytes if you are
            under the applicable minimum age in your region.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="h2">9) Cookies &amp; Similar Technologies</h2>
          <p>
            We use necessary cookies and similar technologies for authentication, security,
            and performance. Where required, we obtain consent for non-essential cookies.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="h2">10) International Transfers</h2>
          <p>
            Where we transfer personal data outside your country, we use lawful transfer
            mechanisms (for example, Standard Contractual Clauses) and require recipients
            to protect your data.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="h2">11) Contact</h2>
          <p>
            For privacy questions or requests:{" "}
            <a href="mailto:jaytayeldev@gmail.com">jaytayeldev@gmail.com</a>
          </p>
        </section>
      </div>
    </Screen>
  );
}

/* ------------------------------ Styles ------------------------------ */
function StyleTag() {
  return (
    <style>{`
.legal-wrap{
  max-width: 860px;
  margin: 0 auto;
}
.legal-header .entity,
.legal-header .intro{
  color: #6B7280;
}
.legal-header .intro a{
  color: #6D5EF9; text-decoration: none; font-weight: 700;
}
.legal-header .intro a:hover{ text-decoration: underline; }
.legal-section{ margin-top: 18px; }
.legal-section p{ margin: 8px 0; line-height: 1.6; }
.legal-section ul{ margin: 8px 0 0 20px; display: grid; gap: 6px; }
.legal-section h3{ margin: 8px 0 6px; font-size: 16px; }
    `}</style>
  );
}
